import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { FileUp, MoreHorizontal, Trash, Users } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";

export const TutorMaterials = () => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { data: workspaceData } = useWorkspaceInformationStore();
  const isWorkspaceInactive =
    workspaceData?.has_user_left_workspace || workspaceData?.is_workspace_blocked_for_user;

  return (
    <Card className="bg-foreground border-none">
      <CardHeader className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <CardTitle>{t("workspaces:materials", "Materials")}</CardTitle>
          <CardDescription>
            {t("workspaces:materialsManagement", "Manage learning materials for your students")}
          </CardDescription>
        </div>
        {!isWorkspaceInactive && (
          <Button size="sm">
            <FileUp className="mr-2 h-4 w-4" />
            {t("workspaces:uploadMaterial", "Upload Material")}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5].map((_, index) => (
            <motion.div
              key={index}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-side-accent hover:bg-muted/50 flex flex-col rounded-md p-4 transition-colors"
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="bg-primary/10 mb-3 flex h-12 w-12 items-center justify-center rounded-md">
                <Icon
                  className="text-muted-foreground h-6 w-6"
                  icon={index % 2 === 0 ? "mdi:file-document-outline" : "mdi:file-pdf-box"}
                />
              </div>
              <h3 className="mb-1 font-medium">
                {t("workspaces:materialTitle", "Material")} #{index + 1}
              </h3>
              <p className="text-muted-foreground mb-3 text-sm">
                {t("workspaces:materialDescription", "Learning resource for your students")}
              </p>
              <div className="mt-auto flex flex-wrap gap-2">
                <Button className="flex-1 sm:flex-none" size="sm" variant="outline">
                  <Icon className="mr-2 h-4 w-4" icon="mdi:download" />
                  {t("workspaces:download", "Download")}
                </Button>
                {!isWorkspaceInactive && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Icon className="mr-2 h-4 w-4" icon="mdi:pencil" />
                        {t("workspaces:edit", "Edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Users className="mr-2 h-4 w-4" />
                        {t("workspaces:assignToStudents", "Assign to Students")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600">
                        <Trash className="mr-2 h-4 w-4" />
                        {t("workspaces:delete", "Delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
